/**
 * REZERVE KULLANICI ADLARI — TEK KAYNAK (EMİR #96.3)
 *
 * ── SORUN ────────────────────────────────────────────────────────────────
 * Dört ayrı liste vardı ve birbirinden SAPMIŞTI:
 *   router RESERVED            src/app/appRouting.ts
 *   kayıt (client)             src/utils/authHelpers.ts
 *   username değişimi (client) src/services/usernameChange.ts
 *   kayıt (server, OTORİTER)   functions/src/auth.ts + firestore.rules
 *
 * Sonuç: router'da rezerve olup kayıt blocklist'inde OLMAYAN adlar
 * (`place`, `sounds`, `clips`, `map`, `discoveries`, `mylive`, `creator`,
 * `card`, `auth`, `embed`, `assets`, `static` + yasal sayfalar) bir kullanıcı
 * tarafından ALINABİLİYORDU — ve o kullanıcının profili `/{username}` ile HİÇ
 * açılamıyordu; kendi profiline bile giremezdi.
 *
 * ── KURAL ────────────────────────────────────────────────────────────────
 * ROUTER_REZERVE ⊆ KAYIT_BLOCKLIST olmak ZORUNDA. Burada bu bir varsayım değil,
 * YAPISAL: `TUM_REZERVE` birleşimdir ve kayıt kontrolü onu kullanır.
 *
 * ⚠️ KOPYALANAN KURAL ÇÜRÜR: `firestore.rules` ve `functions/` bu modülü
 * import EDEMEZ (ayrı paket / farklı dil). Bu yüzden eşitlik bir TESTLE
 * zorlanır (`reservedUsernames.test.ts`) — üç kopyanın kaynağı taranır ve
 * bu listeyle karşılaştırılır. Test, kopyanın çürümesini yakalayan alarmdır.
 *
 * 📌 CANLI TARAMA (2026-07-27, üretim `mylasa-final`, salt-okuma, TAM SAYIM):
 * 28 rezerve adın hiçbiri alınmamış — ne `usernames/{ad}` dokümanı olarak ne de
 * `users` legacy alanlarında (`kullaniciAdi`/`username`/`kullaniciAdiLower`).
 * Toplam 28 kullanıcı → örnekleme değil tam sayım. Göç planı GEREKMİYOR.
 * Kapatmak için en ucuz an: bir ad alındıktan sonra kapatmak, gerçek kullanıcıyı
 * profilinden kilitlemek ya da adını değiştirmesini istemek demek olurdu.
 */

/** Klasik kayıt blocklist'i (server `functions/src/auth.ts` ile birebir). */
export const KAYIT_BLOCKLIST: readonly string[] = [
  "admin", "administrator", "mylasa", "support", "help", "info",
  "contact", "abuse", "security", "privacy", "legal", "terms",
  "api", "app", "web", "mail", "email", "blog", "news", "press",
  "root", "system", "official", "team", "staff", "mod", "moderator",
  "null", "undefined", "test", "demo", "guest", "anonymous",
  "user", "users", "account", "accounts", "profile", "settings",
  "login", "logout", "signup", "register", "password", "forgot",
  "explore", "discover", "feed", "home", "search", "notifications",
  "messages", "inbox", "direct", "stories", "reels", "live",
];

/**
 * Router'ın üst-seviye rotaları — bunlar kullanıcı adı OLAMAZ, çünkü
 * `/{username}` ile çakışırlar (IG deseni).
 * `u`, `p`, `c`, `og` teknik olarak zaten imkânsız (min 3 karakter,
 * authHelpers.ts:42) ama listede DURUYORLAR: kural "imkânsız olduğu için
 * yazmadık" diye bir boşluk bırakmaz; min-uzunluk kuralı bir gün gevşerse
 * burası sessizce açılmaz.
 */
export const ROUTER_REZERVE: readonly string[] = [
  "u", "p", "c", "og", "embed", "explore", "direct", "place", "sounds",
  "clips", "admin", "creator", "mylive", "settings", "map", "search",
  "api", "assets", "static", "stories", "notifications", "discoveries",
  // EMİR #77 — yasal sayfalar (App.tsx pre-auth pathname ile render).
  "gizlilik-politikasi", "kullanim-kosullari", "cerez-politikasi",
  "topluluk-kurallari", "iletisim",
  // PR-V3b — /card/{code} + /auth/action pre-auth şablonları.
  "card", "auth",
  // Composer/kamera + check-in kökü: /create, /create/post, /create/clip,
  // /create/camera, /create/checkin[/{placeId}] (olusturUrl.ts). Check-in için
  // AYRI bir kök açılmadı — tek kelime, üç kopyaya tek satır.
  "create",
  // Anlatım yüzeyleri kökü: /info/{star|gold|mycards|journey}
  // (`src/app/bilgiSayfasiUrl.ts`). "info" ZATEN `KAYIT_BLOCKLIST`teydi →
  // `TUM_REZERVE` birleşimi DEĞİŞMEZ, dolayısıyla `firestore.rules` ve
  // `functions/src/auth.ts` kopyalarına dokunmak GEREKMEDİ. Router'a
  // eklenmesinin sebebi ayrı: çıplak `/info` bir profil sanılmasın.
  "info",
];

/** Kayıt/değişim kontrolünün kullandığı BİRLEŞİM (router ⊆ blocklist garantisi). */
export const TUM_REZERVE: ReadonlySet<string> = new Set<string>([
  ...KAYIT_BLOCKLIST,
  ...ROUTER_REZERVE,
]);

/** Tek giriş noktası — büyük/küçük harf duyarsız. */
export function rezerveMi(ad: unknown): boolean {
  return TUM_REZERVE.has(String(ad || "").trim().toLowerCase());
}
